import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  CallDetailsResponse,
  DashboardSummary,
  LoadCallDetailsPayload,
  LoadCallsPayload,
  RecentCall,
} from "./types";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
}

export interface DashboardState {
  summary: AsyncState<DashboardSummary>;
  calls: AsyncState<RecentCall[]>;
  details: AsyncState<CallDetailsResponse>;
}

export const initialState: DashboardState = {
  summary: { data: null, loading: false, error: false },
  calls: { data: null, loading: false, error: false },
  details: { data: null, loading: false, error: false },
};

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    loadSummary(state) {
      state.summary.loading = true;
      state.summary.error = false;
    },
    summaryLoaded(state, { payload }: PayloadAction<DashboardSummary>) {
      state.summary.data = payload;
      state.summary.loading = false;
      state.summary.error = false;
    },
    loadSummaryError(state) {
      state.summary.loading = false;
      state.summary.error = true;
    },

    loadCalls(state, _action: PayloadAction<LoadCallsPayload | undefined>) {
      state.calls.loading = true;
      state.calls.error = false;
    },
    callsLoaded(state, { payload }: PayloadAction<RecentCall[]>) {
      state.calls.data = payload;
      state.calls.loading = false;
      state.calls.error = false;
    },
    loadCallsError(state) {
      state.calls.loading = false;
      state.calls.error = true;
    },
    setCallDone(
      state,
      { payload }: PayloadAction<{ key: string; isDone: boolean }>,
    ) {
      const record = state.calls.data?.find((c) => c.key === payload.key);
      if (record) record.isDone = payload.isDone;
    },

    loadCallDetails(state, _action: PayloadAction<LoadCallDetailsPayload>) {
      state.details.loading = true;
      state.details.error = false;
      state.details.data = null;
    },
    callDetailsLoaded(state, { payload }: PayloadAction<CallDetailsResponse>) {
      state.details.data = payload;
      state.details.loading = false;
      state.details.error = false;
    },
    loadCallDetailsError(state) {
      state.details.loading = false;
      state.details.error = true;
    },
    callDetailsCleared(state) {
      state.details = { data: null, loading: false, error: false };
    },
  },
});

export const {
  loadSummary,
  summaryLoaded,
  loadSummaryError,
  loadCalls,
  callsLoaded,
  loadCallsError,
  setCallDone,
  loadCallDetails,
  callDetailsLoaded,
  loadCallDetailsError,
  callDetailsCleared,
} = dashboardSlice.actions;

export const { reducer } = dashboardSlice;
